import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { Check, ArrowRight, Plane } from "lucide-react";

const PROFILE_LINK = "https://trackmypackage.app/join/shipcostarica";
const PLANE_IMG = "https://customer-assets.emergentagent.com/job_fly-ship-cr/artifacts/3xo1ic5d_image.png";

const Pricing = () => {
  const { t } = useLang();

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 sm:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left column */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="h-px w-8 bg-brand-red" />
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-brand-red">
                {t.pricing.eyebrow}
              </span>
            </div>
            <h2 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] text-brand-navy leading-[1.05] mb-6">
              {t.pricing.title}
            </h2>
            <p className="text-base sm:text-lg text-brand-navy/75 leading-relaxed max-w-md">
              {t.pricing.tag}
            </p>

            {/* Big price display */}
            <motion.div
              initial={{ opacity: 0, scale: 0.7 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 flex items-end gap-3"
            >
              <div className="font-heading font-black text-[120px] sm:text-[160px] leading-[0.85] tracking-[-0.06em] text-brand-navy">
                {t.pricing.price}
              </div>
              <div className="pb-6 sm:pb-8">
                <div className="text-xl sm:text-2xl font-heading font-bold text-brand-red">
                  / kilo
                </div>
                <div className="text-sm text-brand-navy/60 font-semibold mt-1">
                  {t.pricing.perKilo}
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right column - feature card */}
          <motion.div
            initial={{ opacity: 0, x: 60, y: 30 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <div className="relative rounded-[28px] bg-brand-navy text-brand-cream p-8 sm:p-12 overflow-hidden shadow-[0_30px_60px_-20px_rgba(14,42,86,0.4)]">
              {/* decorative */}
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-brand-red/30 blur-3xl" aria-hidden="true" />
              <motion.img
                src={PLANE_IMG}
                alt=""
                aria-hidden="true"
                draggable={false}
                initial={{ opacity: 0, x: 80, y: 60, rotate: 20 }}
                whileInView={{ opacity: 0.18, x: 0, y: 0, rotate: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="absolute -bottom-6 -right-8 w-56 select-none"
              />

              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full bg-brand-red px-4 py-1.5 mb-7">
                  <Plane size={13} />
                  <span className="text-[11px] font-black uppercase tracking-widest">Air Express</span>
                </div>

                <h3 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mb-8 leading-tight">
                  {t.pricing.tag}
                </h3>

                <ul className="space-y-3.5">
                  {t.pricing.includes.map((it, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 0.5, delay: 0.4 + i * 0.07 }}
                      data-testid={`pricing-feature-${i}`}
                      className="flex items-start gap-3"
                    >
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-cream text-brand-navy flex items-center justify-center mt-0.5">
                        <Check size={13} strokeWidth={3} />
                      </span>
                      <span className="text-base text-brand-cream/95 font-medium">{it}</span>
                    </motion.li>
                  ))}
                </ul>

                <a
                  href={PROFILE_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pricing-cta"
                  className="mt-9 group inline-flex items-center justify-center gap-2 rounded-full bg-brand-cream text-brand-navy font-heading font-bold text-base px-7 py-4 hover:bg-brand-red hover:text-white transition-all shadow-lg"
                >
                  {t.pricing.cta}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </a>

                <div className="mt-6 text-xs text-brand-cream/70 leading-relaxed border-t border-brand-cream/15 pt-4" data-testid="pricing-footnote">
                  {t.pricing.footnote}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
